import { useCallback, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation, useParams } from "react-router";
import { useNavigate } from "./lib/nav";
import { workspaceOfHostname, type Me } from "@omnabase/shared";
import { fetchMe, readCache } from "./lib/api";
import { useDetectTimezone } from "./lib/detectTimezone";
import { landingAfterProof, landingPath, signInAgainPath } from "./lib/signin";
import { holdSignedIn } from "./lib/signedOut";
import { SignedOutSheet } from "./shell/SignedOutSheet";
import { SignInScreen } from "./screens/SignInScreen";
import { InviteScreen } from "./screens/InviteScreen";
import { WorkspaceProvider } from "./shell/store";
import { Tour } from "./shell/Tour";
import { ReportsScreen } from "./screens/reports/ReportsScreen";
import { MoreScreen } from "./screens/MoreScreen";
import { SettingsRoutes } from "./screens/settings/SettingsScreen";
import { InboxScreen } from "./screens/inbox/InboxScreen";
import { UnknownAddressScreen } from "./screens/inbox/UnknownAddressScreen";
import { CustomersScreen } from "./screens/customers/CustomersScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { SearchScreen } from "./screens/SearchScreen";
import { TicketRedirect } from "./screens/inbox/TicketRedirect";
import { KnowledgeScreen } from "./screens/knowledge/KnowledgeScreen";
import { AdminRoutes } from "./screens/admin/AdminRoutes";
import { LegalRedirect } from "./screens/LegalRedirect";
import { ConsentScreen } from "./screens/ConsentScreen";
import { ChatScreen } from "./screens/chat/ChatScreen";
import { ConnectScreen } from "./screens/ConnectScreen";

/** The URL scheme (DECISIONS 2026-09-24): /signin, then /<workspace>/<area>/…. Areas that do not exist yet are placeholders in the shell.
 *  On a workspace's own host (`<workspace>.omnabase.com`, the public addresses of 2026-10-03) the API serves the help centre and the forms'
 *  pages itself; since the form builder's round three (the old `/form/<project>` page retired) the app has nothing there but a not-found line. */
export function App() {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const publicHost = workspaceOfHostname(window.location.hostname);
  // the shared fetch layer keeps answers per session: a different person signed in forgets everything read for the one before (polish round two)
  const loadMe = useCallback(async (how: "cached" | "fresh") => { if (publicHost) { setMe(null); return; } await fetchMe(how).then((m) => { readCache.setSession(m?.user.id ?? ""); setMe(m); }, () => setMe(null)); }, [publicHost]);
  const reloadMe = useCallback(() => loadMe("fresh"), [loadMe]);
  useEffect(() => { void loadMe("cached"); }, [loadMe]);
  holdSignedIn(me?.user.id ?? null); // the forum sync round: a 401 only means "signed out" while the app holds a signed-in person
  if (me === undefined) return null;
  if (publicHost) return <PublicHostNotFound />;
  return (
    <>
    <SignedOutSheet />
    <Routes>
      <Route path="/signin" element={<SignInScreen me={me} onSignedIn={reloadMe} />} />
      <Route path="/invite/:id" element={<InviteScreen me={me} onJoined={reloadMe} />} />
      <Route path="/" element={<Landing me={me} />} />
      <Route path="/legal" element={<LegalRedirect />} />
      {/* M6 round seven: the API renders the legal pages at /legal/:doc; both legal routes sit before the workspace router (legal is a reserved slug) */}
      <Route path="/legal/:doc" element={<LegalRedirect />} />
      {/* browser notifications round two (2026-10-18): the API renders the help articles at /docs/:name (docs is a reserved slug) */}
      <Route path="/docs/*" element={<LegalRedirect />} />
      {/* M9 round two: the OAuth consent page, a plain page before the workspace router (oauth is a reserved slug) */}
      <Route path="/oauth/consent" element={<ConsentScreen me={me} />} />
      {/* M12 round five: "Connect your site", a plain page before the workspace router (connect is a reserved slug); the plugin's data stays after # */}
      <Route path="/connect" element={<ConnectScreen me={me} onSignedIn={reloadMe} />} />
      {/* M6 round four: the admin routes sit before the workspace router (admin is a reserved slug) */}
      <Route path="/admin/*" element={me ? <AdminRoutes me={me} /> : <Navigate to="/signin" replace />} />
      <Route path="/:workspace/*" element={me ? <WorkspaceRoutes me={me} reloadMe={reloadMe} /> : <SignInWithPlace />} />
    </Routes>
    </>
  );
}

/** the forum sync round (2026-11-02, finding 21): a workspace address opened while signed out keeps the place for after the sign-in */
function SignInWithPlace() {
  const { pathname, search } = useLocation();
  return <Navigate to={signInAgainPath(`${pathname}${search}`)} replace />;
}

/** "/" (M11 round one): the landing order of the sign-in (the last workspace while a member, the only one, the welcome step, the chooser) */
function Landing({ me }: { me: Me | null }) {
  if (!me) return <Navigate to="/signin" replace />;
  return <Navigate to={landingPath(landingAfterProof({ workspaces: me.workspaces, lastWorkspace: me.lastWorkspace, named: null, invitation: null }))} replace />;
}

/** /inbox → /inbox/queue, with whatever the address carried (?views=1 from the phone's tab bar). */
function InboxRedirect() { const { search } = useLocation(); return <Navigate to={`queue${search}`} replace />; }

function WorkspaceRoutes({ me, reloadMe }: { me: Me; reloadMe: () => Promise<void> }) {
  const { workspace = "" } = useParams();
  const navigate = useNavigate();
  // the workspace moved to a new address: keep the rest of the path. Answers to the old address can arrive after the first one has
  // already moved the page (the provider asks more than once); a stale one must not rewrite the new address, so it is ignored
  const onMoved = useCallback((slug: string) => {
    const here = window.location.pathname, prefix = `/${workspace}`;
    if (here !== prefix && !here.startsWith(`${prefix}/`)) return;
    navigate(`/${slug}${here.slice(prefix.length)}`, { replace: true });
  }, [navigate, workspace]);
  const onGone = useCallback(() => navigate(me.workspaces[0] ? `/${me.workspaces[0].slug}` : "/signin", { replace: true }), [navigate, me]);
  useDetectTimezone(me, reloadMe); // the working hours round: an empty profile zone is filled once from the browser
  return (
    <WorkspaceProvider key={workspace} slug={workspace} me={me} reloadMe={reloadMe} onMoved={onMoved} onGone={onGone}>
      <Routes>
        <Route index element={<HomeScreen />} />
        <Route path="inbox" element={<InboxRedirect />} />
        <Route path="inbox/unknown-address" element={<UnknownAddressScreen />} />{/* the ticket page and marks round: stray mail, administrators */}
        <Route path="inbox/:view" element={<InboxScreen />} />
        <Route path="inbox/:view/:number" element={<InboxScreen />} />
        <Route path="tickets/:number" element={<TicketRedirect />} />
        <Route path="chat/*" element={<ChatScreen />} />
        <Route path="search" element={<SearchScreen />} />
        <Route path="customers" element={<CustomersScreen />} />
        <Route path="customers/:customerId" element={<CustomersScreen />} />
        <Route path="knowledge/*" element={<KnowledgeScreen />} />
        <Route path="reports/*" element={<ReportsScreen />} />
        <Route path="settings/*" element={<SettingsRoutes />} />
        <Route path="more" element={<MoreScreen />} />
        <Route path="*" element={<Navigate to={`/${workspace}`} replace />} />
      </Routes>
      <Tour />
    </WorkspaceProvider>
  );
}

/** a workspace's own host has no page of the app (round three): a plain line, as the API's own not-found says */
function PublicHostNotFound() {
  return <main className="ob-public"><div className="ob-public__card"><h1 className="ob-public__title">Page not found</h1><p className="ob-public__intro">This address has no page.</p></div></main>;
}
